import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, BadgeCheck, CalendarCheck, ChefHat, MapPin, Sparkles,
  Truck, UtensilsCrossed, PartyPopper, Briefcase, Gem, Gift,
} from "lucide-react";
import { FadeUp, MaskedLines, Eyebrow } from "../components/Reveal";
import { HalalBadge } from "../components/Navbar";
import { ReviewsSection } from "../components/Reviews";
import { MEALS, DELIVERY_NOTICE, fmt } from "../data/site";
import { api, imgSrc } from "../lib/api";
import { useSitePhotos } from "../lib/sitePhotos";
import { useSEO } from "../lib/useSEO";
import { FAQ } from "../components/FAQ";
import { CateringOccasions } from "../components/CateringOccasions";

const MARQUEE_ITEMS = [
  "100% Certified Halal", "DMV Delivery Only", "12 Weekly Rotating Dishes",
  "Corporate · Weddings · Parties", "Cooked in Woodbridge, VA", "First Order: HIPLATE15",
];

const Intro = ({ onDone }) => (
  <motion.div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-parchment"
    initial={{ y: 0 }}
    animate={{ y: "-100%" }}
    transition={{ delay: 1.75, duration: 0.95, ease: [0.76, 0, 0.24, 1] }}
    onAnimationComplete={onDone}
    data-testid="intro-overlay"
  >
    <motion.span
      className="absolute h-72 w-72 rounded-full border-2 border-saffron/50 sm:h-96 sm:w-96"
      initial={{ scale: 0.4, opacity: 0 }}
      animate={{ scale: 1.25, opacity: [0, 1, 0.35] }}
      transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden="true"
    />
    <motion.span
      className="absolute h-72 w-72 rounded-full border border-moss/30 sm:h-96 sm:w-96"
      initial={{ scale: 0.4, opacity: 0 }}
      animate={{ scale: 1.5, opacity: [0, 0.8, 0] }}
      transition={{ duration: 1.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden="true"
    />
    <motion.img
      src="/logo.png"
      alt="HiPlate"
      data-testid="intro-logo"
      className="relative w-60 sm:w-80"
      initial={{ opacity: 0, scale: 0.82, y: 28 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.85, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
    />
    <motion.p
      className="absolute bottom-16 font-mono text-[11px] uppercase tracking-[0.35em] text-moss"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.9, duration: 0.6 }}
    >
      100% Certified Halal · DMV Delivery
    </motion.p>
  </motion.div>
);

const Hero = ({ base = 0.2 }) => {
  const IMG = useSitePhotos();
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-parchment pt-36 lg:pt-44" data-testid="hero">
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-saffron/15 blur-3xl"
      />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 sm:px-8 lg:grid-cols-12 lg:pb-28">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: base }}>
            <HalalBadge />
          </motion.div>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLines
              delay={base + 0.1}
              lines={[
                <>Halal gourmet,</>,
                <><span className="italic text-spice">plated</span> weekly.</>,
                <>Catered <span className="italic text-saffron">grandly.</span></>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: base + 0.65 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted2 sm:text-lg"
          >
            HiPlate is Woodbridge's halal kitchen: twelve rotating chef-crafted meals delivered to
            your door each week, and full-service catering for the moments that matter — boardrooms,
            backyards, and ballrooms across the DMV.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: base + 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-meal-prep"
              onClick={() => navigate("/meal-prep")}
              className="group inline-flex items-center gap-2 rounded-full bg-spice px-7 py-3.5 text-sm font-semibold text-parchment transition-all duration-300 hover:bg-ink hover:shadow-xl hover:shadow-ink/20"
            >
              Explore This Week's Menu
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-catering"
              onClick={() => navigate("/catering")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-7 py-3 text-sm font-semibold text-ink transition-all duration-300 hover:bg-ink hover:text-parchment"
            >
              Plan Your Event
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: base + 1 }}
            className="mt-10 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[["12", "weekly dishes"], ["5+", "meal minimum"], ["3", "catering styles"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-bold text-ink">{n}</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: base + 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-t-[12rem] rounded-b-3xl bg-saffron/25" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-t-[12rem] rounded-b-3xl shadow-2xl shadow-ink/25">
              <img
                src={IMG.heroSpread}
                alt="Overhead spread of HiPlate gourmet halal dishes"
                className="h-[26rem] w-full object-cover sm:h-[30rem]"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: base + 0.95 }}
              className="absolute -bottom-5 -left-4 rounded-2xl border border-ink/10 bg-parchment/90 px-5 py-4 shadow-xl backdrop-blur-md sm:-left-10"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-spice">This week's star</div>
              <div className="mt-1 font-display text-lg font-semibold text-ink">Spiced Chicken Kabsa</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div className="border-y border-ink/10 bg-[#E7EDE1] py-4" data-testid="editorial-marquee">
    <Marquee speed={28} gradient={false}>
      {MARQUEE_ITEMS.map((item) => (
        <span key={item} className="mx-8 flex items-center gap-8 font-display text-lg italic text-moss">
          {item}
          <Sparkles size={14} className="text-spice" />
        </span>
      ))}
    </Marquee>
  </div>
);

const DualFocus = () => {
  const IMG = useSitePhotos();
  return (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="dual-focus">
    <FadeUp>
      <Eyebrow>Two crafts, one kitchen</Eyebrow>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
        Your weeknight routine and your biggest day, cooked to the same standard.
      </h2>
    </FadeUp>
    <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
      <FadeUp className="md:col-span-7">
        <Link to="/meal-prep" data-testid="bento-meal-prep" className="group relative block h-full overflow-hidden rounded-3xl">
          <img src={IMG.mealPrepBox} alt="HiPlate meal prep box" className="h-80 w-full object-cover transition-transform duration-700 group-hover:scale-105 md:h-full md:min-h-[26rem]" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
          <div className="absolute bottom-0 p-7 sm:p-9">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-saffron">Meal Prep Subscriptions</div>
            <h3 className="mt-2 font-display text-2xl font-semibold text-parchment sm:text-3xl">12 rotating dishes. Pick 5 or more. We deliver.</h3>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-saffron">
              Build your bundle <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      </FadeUp>
      <FadeUp delay={0.12} className="md:col-span-5">
        <Link to="/catering" data-testid="bento-catering" className="group relative block h-full overflow-hidden rounded-3xl">
          <img src={IMG.buffetLine} alt="Halal catering buffet spread with grilled chicken, falafel and salads" className="h-80 w-full object-cover transition-transform duration-700 group-hover:scale-105 md:h-full md:min-h-[26rem]" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
          <div className="absolute bottom-0 p-7 sm:p-9">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-saffron">Full-Service Catering</div>
            <h3 className="mt-2 font-display text-2xl font-semibold text-parchment sm:text-3xl">Corporate lunches, parties & weddings.</h3>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-saffron">
              Request a quote <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-4">
        <div className="flex h-full flex-col justify-between rounded-3xl bg-moss p-7 sm:p-8" data-testid="bento-halal">
          <BadgeCheck size={36} className="text-halal" />
          <div>
            <h3 className="mt-6 font-display text-xl font-semibold text-parchment">100% Certified Halal</h3>
            <p className="mt-2 text-sm leading-relaxed text-parchment/70">
              Every protein, every sauce, every dessert. Certified sources, no exceptions, ever.
            </p>
          </div>
        </div>
      </FadeUp>
      <FadeUp delay={0.16} className="md:col-span-4">
        <div className="flex h-full flex-col justify-between rounded-3xl bg-spice p-7 sm:p-8" data-testid="bento-delivery">
          <Truck size={36} className="text-parchment" />
          <div>
            <h3 className="mt-6 font-display text-xl font-semibold text-parchment">Delivery Only</h3>
            <p className="mt-2 text-sm leading-relaxed text-parchment/85">{DELIVERY_NOTICE}</p>
          </div>
        </div>
      </FadeUp>
      <FadeUp delay={0.22} className="md:col-span-4">
        <div className="flex h-full flex-col justify-between rounded-3xl border border-inkline bg-cream p-7 sm:p-8" data-testid="bento-rewards">
          <Gift size={36} className="text-spice" />
          <div>
            <h3 className="mt-6 font-display text-xl font-semibold text-ink">Rewards for Regulars</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted2">
              First order? Take 15% off with HIPLATE15. Returning? Earn HiPlate Points toward free delivery.
            </p>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
  );
};

const pickFeatured = (list) => {
  const chef = list.filter((m) => (m.tags || []).includes("Chef Favorite") || (m.cats || []).includes("chef"));
  const jerk = list.find((m) => m.name === "Jerk Beef");
  const picks = jerk && !chef.includes(jerk) ? [...chef, jerk] : chef;
  return (picks.length ? picks : list).slice(0, 4);
};

const FeaturedDishes = () => {
  const [featured, setFeatured] = useState(() => pickFeatured(MEALS));
  useEffect(() => {
    api.get("/menu")
      .then(({ data }) => { if (Array.isArray(data) && data.length) setFeatured(pickFeatured(data)); })
      .catch(() => {});
  }, []);
  return (
    <section className="bg-cream py-20 lg:py-28" data-testid="featured-dishes">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>From this week's kitchen</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Dishes people reorder by name.
            </h2>
          </div>
          <Link to="/meal-prep" data-testid="featured-view-all" className="group inline-flex items-center gap-2 text-sm font-semibold text-spice">
            View all 12 meals <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((meal, i) => (
            <FadeUp key={meal.id} delay={i * 0.08}>
              <Link to="/meal-prep" data-testid={`featured-dish-${meal.id}`} className="group block">
                <div className="overflow-hidden rounded-2xl">
                  <img src={imgSrc(meal.img)} alt={meal.name} className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <h3 className="font-display text-lg font-semibold leading-snug text-ink group-hover:text-spice">{meal.name}</h3>
                  <span className="shrink-0 font-mono text-sm font-semibold text-spice">{fmt(meal.price)}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-muted2">{meal.desc}</p>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const steps = [
    { icon: UtensilsCrossed, title: "Pick your plates", text: "Choose any 5 or more from 12 weekly rotating halal dishes." },
    { icon: CalendarCheck, title: "Set your schedule", text: "One-time or weekly subscription — subscribers save 10% and earn points." },
    { icon: Truck, title: "We deliver, you feast", text: "Fresh from our Woodbridge kitchen to any door in the DMV. No pickup, no lines." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="how-it-works">
      <FadeUp>
        <Eyebrow>How HiPlate works</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">Three steps to a better week.</h2>
      </FadeUp>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {steps.map((s, i) => (
          <FadeUp key={s.title} delay={i * 0.1}>
            <div className="group relative h-full rounded-3xl border border-inkline bg-parchment p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-spice/40 hover:shadow-xl hover:shadow-spice/10" data-testid={`step-${i + 1}`}>
              <span className="font-mono text-xs text-spice">0{i + 1}</span>
              <s.icon size={30} className="mt-5 text-moss" />
              <h3 className="mt-4 font-display text-xl font-semibold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted2">{s.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const CateringHighlight = () => {
  const IMG = useSitePhotos();
  return (
  <section className="grain bg-ink py-20 text-parchment lg:py-28" data-testid="catering-highlight">
    <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2">
      <FadeUp>
        <Eyebrow light>Full-service catering</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
          Events your guests will <span className="italic text-saffron">taste</span> for years.
        </h2>
        <p className="mt-5 max-w-lg text-base leading-relaxed text-parchment/70">
          From boardroom drop-offs to 300-guest weddings, our culinary team designs halal menus that
          steal the show — plated, staffed, and delivered anywhere in the DMV.
        </p>
        <div className="mt-8">
          <CateringOccasions />
        </div>
        <Link
          to="/catering"
          data-testid="catering-highlight-cta"
          className="mt-9 inline-flex items-center gap-2 rounded-full bg-saffron px-7 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-parchment"
        >
          Get an Instant Estimate <ArrowRight size={16} />
        </Link>
      </FadeUp>
      <FadeUp delay={0.15}>
        <div className="grid grid-cols-2 gap-4">
          <img src={IMG.weddingTable} alt="Elegant HiPlate wedding table" className="h-64 w-full rounded-2xl object-cover sm:h-80" />
          <img src={IMG.chefPlating} alt="HiPlate team serving a corporate halal lunch line" className="mt-10 h-64 w-full rounded-2xl object-cover sm:h-80" />
        </div>
      </FadeUp>
    </div>
  </section>
  );
};


const ZipChecker = () => {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);

  const check = () => {
    const prefix = parseInt(zip.slice(0, 3), 10);
    if (!/^\d{5}$/.test(zip)) {
      setResult({ ok: false, text: "Enter a valid 5-digit ZIP code." });
    } else if (prefix >= 200 && prefix <= 223) {
      setResult({ ok: true, text: "Great news — you're in our DMV delivery zone. We bring it to your door." });
    } else {
      setResult({ ok: false, text: "You're outside our current DMV delivery area — join the list and we'll let you know when we expand." });
    }
  };

  return (
    <section className="bg-cream py-20 lg:py-24" data-testid="delivery-zone">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2">
        <FadeUp>
          <Eyebrow>Delivery only — no pickup</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Do we deliver to your door?
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-muted2">
            Based in Woodbridge, VA, we deliver across Washington DC, Maryland, and Northern Virginia.
            Check your ZIP below.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Washington DC", "Arlington", "Alexandria", "Fairfax", "Rockville", "Silver Spring", "Woodbridge", "Bethesda"].map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-inkline bg-parchment px-3.5 py-1.5 text-xs font-medium text-ink/75">
                <MapPin size={12} className="text-spice" /> {c}
              </span>
            ))}
          </div>
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="rounded-3xl border border-inkline bg-parchment p-8 shadow-lg shadow-ink/5">
            <label htmlFor="zip-input" className="font-mono text-xs uppercase tracking-[0.2em] text-muted2">Your ZIP code</label>
            <div className="mt-3 flex gap-3">
              <input
                id="zip-input"
                data-testid="zip-input"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                onKeyDown={(e) => e.key === "Enter" && check()}
                placeholder="22192"
                inputMode="numeric"
                className="w-full rounded-xl border border-inkline bg-cream px-4 py-3 text-lg font-semibold tracking-widest text-ink outline-none transition-colors focus:border-spice"
              />
              <button
                data-testid="zip-check-btn"
                onClick={check}
                className="rounded-xl bg-ink px-6 py-3 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-spice"
              >
                Check
              </button>
            </div>
            {result && (
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="zip-result"
                className={`mt-4 flex items-start gap-2 text-sm font-medium ${result.ok ? "text-halal" : "text-spice"}`}
              >
                <BadgeCheck size={16} className="mt-0.5 shrink-0" /> {result.text}
              </motion.p>
            )}
            <p className="mt-5 border-t border-inkline pt-4 text-xs leading-relaxed text-muted2">{DELIVERY_NOTICE}</p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  useSEO({ title: "HiPlate — 100% Halal Meal Prep & Catering | DMV Delivery", description: "Gourmet 100% halal weekly meal prep (5-meal minimum) and full-service catering for corporate events, parties and weddings. Delivered across DC, Maryland and Northern Virginia.", path: "/" });
  const [showIntro, setShowIntro] = useState(() => {
    try {
      return !sessionStorage.getItem("hiplate-intro-seen");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (showIntro) {
      try {
        sessionStorage.setItem("hiplate-intro-seen", "1");
      } catch {}
    }
  }, [showIntro]);

  const heroBase = showIntro ? 2.3 : 0.2;

  return (
    <main>
      <AnimatePresence>
        {showIntro && <Intro onDone={() => setShowIntro(false)} />}
      </AnimatePresence>
      <Hero base={heroBase} />
      <EditorialMarquee />
      <DualFocus />
      <FeaturedDishes />
      <HowItWorks />
      <CateringHighlight />
      <ReviewsSection />
      <FAQ />
      <ZipChecker />
    </main>
  );
}
